import { ExternalLinkIcon } from 'lucide-react'

import { CopyCurrentUrlButton } from '~/components/CopyCurrentUrlButton'
import { Button } from '~/components/component-library'
import { LabelValue } from '~/components/component-library/LabelValue'
import { Timestamp } from '~/components/component-library/Timestamp'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { TransferIcon } from '~/components/smart-accounts/InboundTransactionDialog'
import { SecondaryDisplayButtons } from '~/components/txn-display/SecondaryDisplayButtons'
import { TransactionMemo } from '~/components/txn-display/memo'
import { TransactionProperties } from '~/components/txn-display/properties'
import { StateChangeList } from '~/components/txn-display/state-change/components/StateChangeList'
import { useTransferStateChangeItems } from '~/components/txn-display/state-change/hooks/useTransferStateChangeItems'
import { CheckCircle } from '~/icons/CheckCircle'
import { OrgAssetTransfer } from '~/types'
import { getExplorerTransactionLinkChainId } from '~/utils/addresses'
import { getTxnDetailsUrl } from '~/utils/tenderly'

export const AssetTransferDisplay = ({
  transfer,
  includeTitleAndIcon = false,
}: {
  transfer: OrgAssetTransfer
  includeTitleAndIcon?: boolean
}) => {
  const { displayItems, isLoading } = useTransferStateChangeItems({
    ownedAddress: transfer.smartAccountAddress,
    from: transfer.from,
    to: transfer.to,
    transferType: transfer.transferType,
    tokenAddress: transfer.tokenAddress,
    transactionHash: transfer.transactionHash,
    chainId: transfer.chainId,
    transactionTime: transfer.transactionTime,
  })

  const explorerLink = transfer.transactionHash
    ? getExplorerTransactionLinkChainId(
        transfer.transactionHash,
        transfer.chainId,
      )
    : undefined

  const tenderlyUrl = transfer.transactionHash
    ? getTxnDetailsUrl({
        chainId: transfer.chainId,
        txnHash: transfer.transactionHash,
      })
    : undefined

  const secondaryDisplayButtons = (
    <SecondaryDisplayButtons
      isShowingCalls={false}
      onToggleCalls={() => {}}
      hasDecodedCalls={false}
      tenderlyUrl={tenderlyUrl}
    />
  )

  return (
    <div className="flex w-full flex-col gap-4">
      {includeTitleAndIcon && (
        <>
          <div className="-mb-2 flex">
            <TransferIcon transfer={transfer} size="lg" />
          </div>
          <TransactionTitle
            transaction={transfer}
            className="mt-2 w-full flex-grow text-wrap break-words text-left text-lg font-medium"
            textWrap
          />
        </>
      )}
      <TransactionMemo transaction={transfer} />
      {(isLoading || displayItems.length > 0) && (
        <StateChangeList
          stateChangeItems={displayItems}
          isLoading={isLoading}
          isShowingCalls={false}
          secondaryDisplayButtons={secondaryDisplayButtons}
        />
      )}
      <LabelValue
        label="Status"
        size="small"
        value={
          <div className="flex items-center gap-1.5">
            <CheckCircle className="size-4 text-green-500" />
            <span>Completed</span>
            <span className="text-secondary">&middot;</span>
            <Timestamp timestamp={transfer.transactionTime} type="relative" />
          </div>
        }
      />
      <TransactionProperties properties={transfer.properties} />
      <div className="mt-4 flex w-full justify-end gap-4">
        <div>
          <CopyCurrentUrlButton trackingId={`transaction.${transfer.id}`} />
        </div>
        {explorerLink && (
          <Button
            variant="secondary"
            prefix={<ExternalLinkIcon />}
            href={{ path: explorerLink, target: 'tab' }}
          >
            Open explorer
          </Button>
        )}
      </div>
    </div>
  )
}
